import { motion, useReducedMotion } from "framer-motion";
import { Box, useBreakpointValue, useColorModeValue } from "@chakra-ui/react";

import { CITY_POINTS, GRATICULE_PATH, LAND_PATH, MAP_VIEWBOX } from "../../assets/worldMapData";
import { ease } from "../../constants";

// Indie shops — one per region so pins stay readable.
const SHOP_LABELS = {
    "Portland": "Coava Coffee",
    "New York": "Sey Coffee",
    "Mexico City": "Buna",
    "São Paulo": "Coffee Lab",
    "London": "Workshop Coffee",
    "Cape Town": "Truth Coffee",
    "Addis Ababa": "Tomoca",
    "Mumbai": "Subko Coffee",
    "Bangkok": "Factory Coffee",
    "Tokyo": "Onibus Coffee",
    "Melbourne": "Proud Mary",
};

// Skip labels under the hero plate only — keep flanks, top (London), and south (Cape Town).
const shouldShowLabel = (c) => {
    if (!SHOP_LABELS[c.name]) return false;
    const underHero = c.x >= 420 && c.x <= 580 && c.y >= 130 && c.y <= 320;
    return !underHero;
};

const labelOffsets = (c) => {
    const left = c.x < 500;
    const nearTop = c.y < 140;
    const nearBottom = c.y > 340;
    const dx = left ? -10 : 10;
    const anchor = left ? "end" : "start";

    // Keep top pin names (London) readable — sit below the pin, above the hero.
    if (nearTop) {
        return { dx, anchor, nameY: c.y + 14, cityY: c.y + 23 };
    }
    if (nearBottom) {
        return { dx, anchor, nameY: c.y - 14, cityY: c.y - 5 };
    }
    return { dx, anchor, nameY: c.y - 1, cityY: c.y + 8 };
};

const HomeMapBackdrop = ({ showPins = true }) => {
    const reduce = useReducedMotion();
    // Labels from small tablets up; phones stay pin-only to avoid clutter.
    const showLabels = useBreakpointValue({ base: false, sm: true, md: true, lg: true });

    const landDot     = useColorModeValue("rgba(139,69,19,0.16)",  "rgba(200,169,126,0.14)");
    const graticule   = useColorModeValue("rgba(139,69,19,0.07)",  "rgba(200,169,126,0.07)");
    const pinCore     = useColorModeValue("#8b3e12",               "#d8b98a");
    const pinGlow     = useColorModeValue("rgba(139,62,18,0.38)",  "rgba(200,169,126,0.42)");
    const labelStrong = useColorModeValue("rgba(90,45,20,0.78)",   "rgba(232,220,200,0.65)");
    const labelDim    = useColorModeValue("rgba(90,45,20,0.48)",   "rgba(232,220,200,0.4)");
    const mapOpacity  = useColorModeValue(0.95, 0.9);

    return (
        <Box aria-hidden="true" position="absolute" inset={0} overflow="hidden">
            <Box
                position="absolute"
                inset={0}
                opacity={mapOpacity}
            >
                <svg
                    width="100%"
                    height="100%"
                    viewBox={MAP_VIEWBOX}
                    preserveAspectRatio="xMidYMid slice"
                    style={{ display: "block" }}
                >
                    <defs>
                        <pattern id="kmap-dots" width="7.4" height="7.4" patternUnits="userSpaceOnUse">
                            <circle cx="1.1" cy="1.1" r="1.05" fill={landDot} />
                        </pattern>
                        <radialGradient id="kmap-glow">
                            <stop offset="0%" stopColor={pinGlow} />
                            <stop offset="100%" stopColor={pinGlow} stopOpacity="0" />
                        </radialGradient>
                    </defs>

                    <path d={GRATICULE_PATH} fill="none" stroke={graticule} strokeWidth="0.6" />

                    <path d={LAND_PATH} fill="url(#kmap-dots)" />

                    <motion.g
                        initial={false}
                        animate={{ opacity: showPins ? 1 : 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        style={{ pointerEvents: "none" }}
                    >
                        {CITY_POINTS.map((c, i) => (
                            <g key={c.name} transform={`translate(${c.x} ${c.y})`}>
                                <circle r="11" fill="url(#kmap-glow)" />
                                {!reduce && showPins && (
                                    <motion.circle
                                        r="2.8"
                                        fill="none"
                                        stroke={pinCore}
                                        strokeWidth="1"
                                        initial={{ scale: 1, opacity: 0.35 }}
                                        animate={{ scale: [1, 2.4], opacity: [0.35, 0] }}
                                        transition={{ duration: 5, delay: (i % 8) * 0.6, repeat: Infinity, ease, repeatDelay: 2 }}
                                    />
                                )}
                                <circle r="2.1" fill={pinCore} />
                            </g>
                        ))}

                        {showLabels && CITY_POINTS.filter(shouldShowLabel).map((c, i) => {
                            const { dx, anchor, nameY, cityY } = labelOffsets(c);
                            return (
                                <motion.g
                                    key={`${c.name}-label`}
                                    initial={false}
                                    animate={{ opacity: showPins ? 1 : 0 }}
                                    transition={{ duration: 0.25, ease: "easeOut" }}
                                >
                                    <text
                                        x={c.x + dx}
                                        y={nameY}
                                        textAnchor={anchor}
                                        fontFamily="var(--chakra-fonts-body)"
                                        fontSize="8.5"
                                        fontWeight="600"
                                        fill={labelStrong}
                                        letterSpacing="0.01em"
                                    >
                                        {SHOP_LABELS[c.name]}
                                    </text>
                                    <text
                                        x={c.x + dx}
                                        y={cityY}
                                        textAnchor={anchor}
                                        fontFamily="var(--chakra-fonts-body)"
                                        fontSize="6"
                                        fill={labelDim}
                                        letterSpacing="0.14em"
                                    >
                                        {c.name.toUpperCase()}
                                    </text>
                                </motion.g>
                            );
                        })}
                    </motion.g>
                </svg>
            </Box>
        </Box>
    );
};

export default HomeMapBackdrop;
